import { urlMap } from '../../../utils';

/**
 * Utility helpers for connecting to the agent log SSE stream.
 * Both AgentPanel and ChapterAgentPanel use these to manage the
 * EventSource lifecycle so the AgentLogs UI component stays pure.
 *
 * Usage in a class component:
 *
 *   state = { agentLogs: [], logsExpanded: true };
 *
 *   connectLogs(executionId) {
 *     this.logSource = connectLogStream(executionId, (event) => {
 *       this.setState(s => ({ agentLogs: [...s.agentLogs, event] }));
 *       parseLogEvent(event, {
 *         onStepStart: this.handleStepStart,
 *         onCheckpointReached: this.handleCheckpointReached,
 *         onExecutionComplete: this.handleExecutionComplete,
 *       });
 *     });
 *   }
 *
 *   disconnectLogs() {
 *     disconnectLogStream(this.logSource);
 *     this.logSource = null;
 *   }
 */

/**
 * Open an EventSource to the agent log endpoint.
 *
 * @param {string} executionId
 * @param {(event: object) => void} onLogEvent  Called for every parsed log event
 * @returns {EventSource}
 */
export function connectLogStream(executionId, onLogEvent) {
  let url = urlMap.getAgentLogs(executionId);

  const token = localStorage.getItem('token');
  if (token) {
    url += `?token=${encodeURIComponent(token)}`;
  }

  const es = new EventSource(url);

  es.onmessage = (event) => {
    try {
      onLogEvent(JSON.parse(event.data));
    } catch (err) {
      console.error('[agentLogStream] Failed to parse log event:', err);
    }
  };

  es.onerror = (error) => {
    console.error('[agentLogStream] EventSource error:', error);
  };

  return es;
}

/**
 * Close an EventSource previously opened by connectLogStream.
 *
 * @param {EventSource | null} eventSource
 */
export function disconnectLogStream(eventSource) {
  if (eventSource) {
    eventSource.close();
  }
}

/**
 * Inspect a log event and fire the appropriate callback.
 *
 * @param {object} logEvent
 * @param {{ onStepStart?, onStepCompleted?, onCheckpointReached?, onExecutionComplete? }} callbacks
 */
export function parseLogEvent(logEvent, callbacks) {
  const msg = logEvent.message || '';
  const meta = logEvent.metadata || {};

  // Step start — "Executing step: StepName"
  if (msg.includes('Executing step:') && meta.step) {
    callbacks.onStepStart?.({
      step: meta.step,
      executionId: logEvent.executionId,
    });
  }

  // Step completed — "Step StepName completed successfully"
  if (msg.includes('completed successfully') && meta.step && meta.success !== undefined) {
    callbacks.onStepCompleted?.({
      step: meta.step,
      success: meta.success,
      duration: meta.duration,
      executionId: logEvent.executionId,
    });
  }

  // Checkpoint / pause
  if (msg.includes('requires checkpoint, pausing execution')) {
    callbacks.onCheckpointReached?.({
      step: meta.step,
      executionId: logEvent.executionId,
    });
  }

  // Execution complete
  if (meta.completed) {
    callbacks.onExecutionComplete?.({
      success: meta.success !== false,
      error: meta.error,
      executionId: logEvent.executionId,
    });
  }
}
